/**
 * SEO de la tienda: títulos, descripciones y datos estructurados (schema.org)
 * de cada página. Es la única fuente: la usan la app (useSeo, al navegar) y
 * el script scripts/prerender.mjs (HTML real por página para Google y los
 * buscadores de IA que no ejecutan JavaScript).
 *
 * Sin imports a propósito: Node lo carga directo desde el script.
 */

// ─── Datos del negocio ────────────────────────────────────────────────────────
// Lo que no se sabe con certeza se deja vacío: los campos vacíos no se publican.

export const NEGOCIO = {
  nombre: 'ONFASE',
  url: 'https://onfase.com',
  logo: 'https://onfase.com/logo.png',
  imagen: 'https://onfase.com/og-image.jpg',
  descripcion:
    'Tienda de paneles solares y sistemas solares en La Habana, Cuba: paneles, inversores, baterías, kits de respaldo, estructuras y materiales eléctricos, con servicio de instalación y asesoría técnica. También instalamos cámaras de seguridad y sistemas eléctricos residenciales.',
  /** Otros nombres con que la gente busca el negocio */
  otrosNombres: ['ONFASE Tienda Solar', 'Tienda de paneles solares ONFASE'],
  lema: 'Tienda de paneles y sistemas solares en La Habana, Cuba',
  /** Teléfono en formato internacional, p. ej. "+5350378286". Vacío = no se publica. */
  telefono: '',
  email: '',
  /** Dirección física. Vacía = solo se publica la zona de servicio. */
  direccion: { calle: '', municipio: '', provincia: 'La Habana', pais: 'CU' },
  zonas: ['La Habana', 'Cuba'],
  /** Perfiles públicos (Facebook, Instagram, etc.) */
  redes: [] as string[],
};

/** Nombre del sitio al final de cada título */
const SUFIJO = ` | ${NEGOCIO.nombre}`;

// ─── Tipos ────────────────────────────────────────────────────────────────────

export interface ProductoSeo {
  id: string;
  nombre: string;
  descripcion: string;
  categoria: string;
  precioUsd: number;
  disponible: boolean;
  /** URL absoluta de la foto, o null si no tiene */
  imagen: string | null;
}

export interface PaginaSeo {
  titulo: string;
  descripcion: string;
  /** Ruta desde la raíz, p. ej. "/products/panel-550w" */
  ruta: string;
  imagen?: string;
  jsonLd: Record<string, unknown>[];
  noIndex?: boolean;
}

// ─── Utilidades ───────────────────────────────────────────────────────────────

export function slugCategoria(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/** Una línea de hasta `max` caracteres reales, cortada en palabra. */
export function resumen(texto: string, max = 155): string {
  const plano = texto.replace(/\s+/g, ' ').trim();
  const chars = Array.from(plano);
  if (chars.length <= max) return plano;
  const corte = chars.slice(0, max - 1).join('');
  return `${corte.slice(0, corte.lastIndexOf(' ') > 60 ? corte.lastIndexOf(' ') : corte.length)}…`;
}

export function urlAbsoluta(ruta: string): string {
  return `${NEGOCIO.url}${ruta === '/' ? '/' : ruta}`;
}

function precio(n: number): string {
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} USD`;
}

// ─── Datos estructurados ──────────────────────────────────────────────────────

const ID_NEGOCIO = `${NEGOCIO.url}/#negocio`;

/** El negocio: instalador eléctrico/solar con zona de servicio en La Habana. */
export function jsonNegocio(): Record<string, unknown> {
  const d = NEGOCIO.direccion;
  return limpio({
    '@context': 'https://schema.org',
    '@type': ['Electrician', 'Store'],
    '@id': ID_NEGOCIO,
    name: NEGOCIO.nombre,
    alternateName: NEGOCIO.otrosNombres,
    slogan: NEGOCIO.lema,
    url: NEGOCIO.url,
    logo: NEGOCIO.logo,
    image: NEGOCIO.imagen,
    description: NEGOCIO.descripcion,
    telephone: NEGOCIO.telefono,
    email: NEGOCIO.email,
    address: {
      '@type': 'PostalAddress',
      streetAddress: d.calle,
      addressLocality: d.municipio,
      addressRegion: d.provincia,
      addressCountry: d.pais,
    },
    areaServed: [
      { '@type': 'City', name: 'La Habana' },
      { '@type': 'Country', name: 'Cuba' },
    ],
    knowsAbout: [
      'Instalación de paneles solares',
      'Sistemas fotovoltaicos',
      'Inversores híbridos',
      'Baterías de litio LiFePO4',
      'Sistemas de respaldo eléctrico',
      'Estructuras y terrazas para paneles solares',
      'Instalaciones eléctricas',
      'Diseño de sistemas eléctricos residenciales',
      'Cámaras de seguridad y videovigilancia',
    ],
    brand: ['SAKO', 'Growatt', 'Infini Solar', 'Must'].map((name) => ({ '@type': 'Brand', name })),
    sameAs: NEGOCIO.redes,
  });
}

export function jsonSitioWeb(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: NEGOCIO.nombre,
    alternateName: NEGOCIO.otrosNombres,
    url: `${NEGOCIO.url}/`,
    inLanguage: 'es-CU',
    publisher: { '@id': ID_NEGOCIO },
    potentialAction: {
      '@type': 'SearchAction',
      target: `${NEGOCIO.url}/products?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };
}

export function jsonMigas(migas: { nombre: string; ruta: string }[]): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: migas.map((m, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: m.nombre,
      item: urlAbsoluta(m.ruta),
    })),
  };
}

export function jsonFaq(preguntas: { q: string; a: string }[]): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: preguntas.map((p) => ({
      '@type': 'Question',
      name: p.q,
      acceptedAnswer: { '@type': 'Answer', text: p.a },
    })),
  };
}

function jsonServicio(nombre: string, descripcion: string, ruta: string): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: nombre,
    serviceType: nombre,
    description: descripcion,
    url: urlAbsoluta(ruta),
    provider: { '@id': ID_NEGOCIO },
    areaServed: [
      { '@type': 'City', name: 'La Habana' },
      { '@type': 'Country', name: 'Cuba' },
    ],
  };
}

export function jsonProducto(p: ProductoSeo): Record<string, unknown> {
  return limpio({
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: p.nombre,
    description: p.descripcion || `${p.nombre} disponible en ONFASE, La Habana, Cuba.`,
    sku: p.id,
    image: p.imagen ?? undefined,
    category: p.categoria,
    url: urlAbsoluta(`/products/${p.id}`),
    offers: {
      '@type': 'Offer',
      price: p.precioUsd.toFixed(2),
      priceCurrency: 'USD',
      availability: p.disponible ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
      itemCondition: 'https://schema.org/NewCondition',
      url: urlAbsoluta(`/products/${p.id}`),
      areaServed: { '@type': 'City', name: 'La Habana' },
      seller: { '@id': ID_NEGOCIO },
    },
  });
}

/** Quita campos vacíos ("" / [] / objetos sin datos) para no publicar datos falsos. */
function limpio<T>(valor: T): T {
  if (Array.isArray(valor)) {
    return valor.map(limpio).filter((v) => !vacio(v)) as T;
  }
  if (valor && typeof valor === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(valor)) {
      const l = limpio(v);
      if (!vacio(l)) out[k] = l;
    }
    return out as T;
  }
  return valor;
}

function vacio(v: unknown): boolean {
  if (v === undefined || v === null || v === '') return true;
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === 'object') {
    const claves = Object.keys(v as object).filter((k) => k !== '@type');
    return claves.length === 0;
  }
  return false;
}

// ─── Preguntas frecuentes (se muestran en la portada) ─────────────────────────

export const FAQ_INICIO = [
  {
    q: '¿Quién instala paneles solares en La Habana?',
    a: 'ONFASE instala sistemas de paneles solares completos en La Habana: evaluamos tu consumo y espacio sin costo, diseñamos el sistema, suministramos paneles, inversores y baterías, y hacemos el montaje, el cableado, la configuración y la puesta en marcha. Llevamos más de 100 instalaciones realizadas.',
  },
  {
    q: '¿Dónde hay una tienda de paneles solares en La Habana?',
    a: 'ONFASE es una tienda de paneles solares y sistemas solares en La Habana, Cuba. En nuestra tienda online ves los precios en USD de paneles, inversores, baterías y kits completos, haces tu pedido por WhatsApp y te lo enviamos dentro de La Habana, con asesoría técnica y servicio de instalación.',
  },
  {
    q: '¿Dónde comprar un sistema solar completo en Cuba?',
    a: 'En la tienda de ONFASE hay sistemas solares completos listos para instalar: kits "todo en uno" con inversor híbrido, baterías de litio y paneles solares, además de cada equipo por separado. Si no sabes qué sistema necesitas, te asesoramos gratis por WhatsApp según tu consumo.',
  },
  {
    q: '¿Qué equipos para instalaciones eléctricas venden?',
    a: 'Vendemos todo lo necesario para instalaciones eléctricas y solares en Cuba: paneles solares, inversores híbridos, baterías de litio, kits de respaldo "todo en uno", cables solares, breakers AC y DC, protectores de voltaje, conectores MC4 y estructuras.',
  },
  {
    q: '¿Hacen envíos de equipos solares en La Habana?',
    a: 'Sí, realizamos envíos de paneles solares, inversores, baterías y materiales eléctricos en La Habana. Escríbenos por WhatsApp para coordinar el envío a tu dirección.',
  },
  {
    q: '¿Qué sistema solar necesito para mi casa en Cuba?',
    a: 'Depende de tu consumo y de cuántas horas de respaldo quieres durante los apagones. Un kit "todo en uno" de 6 kW con baterías de 15 kWh cubre una casa con refrigerador, luces, ventiladores y televisor. Te hacemos la evaluación gratis por WhatsApp.',
  },
  {
    q: '¿Cuáles son los mejores inversores solares disponibles en Cuba?',
    a: 'Trabajamos con inversores de marcas reconocidas como SAKO, Growatt, Infini Solar y Must: inversores híbridos de onda pura con salida 110/220 V, adecuados para las condiciones eléctricas de Cuba.',
  },
];

// ─── Páginas ──────────────────────────────────────────────────────────────────

export function seoInicio(): PaginaSeo {
  return {
    titulo: 'Tienda de paneles solares en La Habana, Cuba | ONFASE',
    descripcion:
      'Tienda de paneles solares y sistemas solares en La Habana, Cuba: inversores, baterías y kits de respaldo para apagones, con instalación. Pide por WhatsApp.',
    ruta: '/',
    imagen: NEGOCIO.imagen,
    jsonLd: [jsonNegocio(), jsonSitioWeb(), jsonFaq(FAQ_INICIO)],
  };
}

export function seoProductos(): PaginaSeo {
  return {
    titulo: `Tienda de sistemas solares en La Habana, Cuba${SUFIJO}`,
    descripcion:
      'Precios de paneles solares, inversores, baterías de litio y sistemas solares completos en nuestra tienda de La Habana, Cuba. Envío y asesoría por WhatsApp.',
    ruta: '/products',
    jsonLd: [jsonMigas([{ nombre: 'Inicio', ruta: '/' }, { nombre: 'Productos', ruta: '/products' }])],
  };
}

export function seoCategoria(nombre: string, cantidad: number): PaginaSeo {
  const ruta = `/category/${slugCategoria(nombre)}`;
  return {
    titulo: `${nombre} – tienda en La Habana, Cuba${SUFIJO}`,
    descripcion: `${cantidad > 0 ? `${cantidad} productos` : 'Productos'} de ${nombre.toLowerCase()} con precio en USD y entrega en La Habana. Asesoría técnica de ONFASE por WhatsApp.`,
    ruta,
    jsonLd: [
      jsonMigas([
        { nombre: 'Inicio', ruta: '/' },
        { nombre: 'Productos', ruta: '/products' },
        { nombre, ruta },
      ]),
    ],
  };
}

export function seoProducto(p: ProductoSeo): PaginaSeo {
  const ruta = `/products/${p.id}`;
  const desc = p.descripcion
    ? resumen(`${p.nombre}: ${p.descripcion}`)
    : resumen(`${p.nombre} en La Habana, Cuba por ${precio(p.precioUsd)}. Asesoría técnica y entrega en La Habana. Pide por WhatsApp a ONFASE.`);
  const migas = [
    { nombre: 'Inicio', ruta: '/' },
    { nombre: 'Productos', ruta: '/products' },
  ];
  if (p.categoria) migas.push({ nombre: p.categoria, ruta: `/category/${slugCategoria(p.categoria)}` });
  migas.push({ nombre: p.nombre, ruta });
  return {
    titulo: `${p.nombre} – ${precio(p.precioUsd)}${SUFIJO}`,
    descripcion: desc,
    ruta,
    imagen: p.imagen ?? undefined,
    jsonLd: [jsonProducto(p), jsonMigas(migas)],
  };
}

const DESC_PANELES =
  'Instalación de paneles solares en La Habana: evaluación gratis, diseño del sistema, montaje, cableado, configuración de inversores y puesta en marcha, con garantía y seguimiento.';

export function seoPaneles(): PaginaSeo {
  return {
    titulo: `Instalación de paneles solares en La Habana${SUFIJO}`,
    descripcion: DESC_PANELES,
    ruta: '/servicios/paneles',
    jsonLd: [
      jsonServicio('Instalación de paneles solares', DESC_PANELES, '/servicios/paneles'),
      jsonMigas([
        { nombre: 'Inicio', ruta: '/' },
        { nombre: 'Instalación de paneles solares', ruta: '/servicios/paneles' },
      ]),
    ],
  };
}

const DESC_TERRAZAS =
  'Fabricación e instalación de terrazas y estructuras metálicas para paneles solares en La Habana, con la orientación e inclinación adecuadas para Cuba.';

export function seoTerrazas(): PaginaSeo {
  return {
    titulo: `Terrazas y estructuras para paneles solares en La Habana${SUFIJO}`,
    descripcion: DESC_TERRAZAS,
    ruta: '/servicios/terrazas',
    jsonLd: [
      jsonServicio('Terrazas y estructuras para paneles solares', DESC_TERRAZAS, '/servicios/terrazas'),
      jsonMigas([
        { nombre: 'Inicio', ruta: '/' },
        { nombre: 'Terrazas para paneles solares', ruta: '/servicios/terrazas' },
      ]),
    ],
  };
}

export const DESC_SEGURIDAD =
  'Instalación de cámaras de seguridad y sistemas de vigilancia en La Habana: cámaras HD, grabación continua, vista desde tu celular y respaldo para que sigan funcionando en los apagones.';

export function seoSeguridad(): PaginaSeo {
  return {
    titulo: `Instalación de cámaras de seguridad en La Habana${SUFIJO}`,
    descripcion: DESC_SEGURIDAD,
    ruta: '/servicios/seguridad',
    jsonLd: [
      jsonServicio('Instalación de cámaras y sistemas de seguridad', DESC_SEGURIDAD, '/servicios/seguridad'),
      jsonMigas([
        { nombre: 'Inicio', ruta: '/' },
        { nombre: 'Cámaras y sistemas de seguridad', ruta: '/servicios/seguridad' },
      ]),
    ],
  };
}

export const DESC_ELECTRICO =
  'Diseño desde cero e instalación de sistemas eléctricos residenciales en La Habana: cálculo de cargas, plano de circuitos, pizarra con breakers, cableado, puesta a tierra y casa preparada para energía solar.';

export function seoElectrico(): PaginaSeo {
  return {
    titulo: `Diseño e instalación eléctrica residencial en La Habana${SUFIJO}`,
    descripcion: DESC_ELECTRICO,
    ruta: '/servicios/electrico',
    jsonLd: [
      jsonServicio('Diseño de sistemas eléctricos residenciales', DESC_ELECTRICO, '/servicios/electrico'),
      jsonMigas([
        { nombre: 'Inicio', ruta: '/' },
        { nombre: 'Diseño eléctrico residencial', ruta: '/servicios/electrico' },
      ]),
    ],
  };
}

export function seoNosotros(): PaginaSeo {
  return {
    titulo: `Sobre nosotros: instaladores solares en La Habana${SUFIJO}`,
    descripcion:
      'Somos ONFASE, un equipo de La Habana especializado en energía solar, instalaciones eléctricas y estructuras metálicas, con más de 100 instalaciones realizadas.',
    ruta: '/about',
    jsonLd: [jsonNegocio()],
  };
}

export function seoContacto(): PaginaSeo {
  return {
    titulo: `Contacto: presupuesto de paneles solares${SUFIJO}`,
    descripcion:
      'Escríbenos por WhatsApp para una evaluación gratis de tu sistema solar en La Habana: te decimos qué equipos necesitas según tu consumo y tu presupuesto.',
    ruta: '/contact',
    jsonLd: [jsonNegocio()],
  };
}

export function seoNoIndexar(titulo: string, ruta: string): PaginaSeo {
  return { titulo: `${titulo}${SUFIJO}`, descripcion: '', ruta, jsonLd: [], noIndex: true };
}
